import {
  ChangeEvent,
  FormEvent,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal } from "react-bootstrap";
import {
  DEFAULT_UNMATCHED_CPT_POLICY,
  ServiceProvider,
  UnmatchedCptPolicyId,
} from "../../../utils/integrationConfigData";
import { ProviderIntegrationApi } from "../../../service/Api/ProviderIntegrationApi";
import {
  BulkAddCptCodesResponseApi,
} from "../types/providerIntegrationtypes";
import { useAuditUserId } from "../../../hooks/useAuditUserId";
import {
  buildAddResultSummary,
  buildSkippedDownloadFilename,
  downloadTextFile,
  formatAddResultMessage,
  mergeAddResults,
} from "../../../utils/cptAddResultExport";
import UnmatchedCptCodes from "./UnmatchedCptCodes";

interface CptCodesPanelProps {
  provider: ServiceProvider;
  importNonce?: number;
  compact?: boolean;
  showUnmatchedPolicy?: boolean;
  defaultProviderLabel?: string;
  unmatchedPolicy?: UnmatchedCptPolicyId;
  onUnmatchedPolicyChange?: (value: UnmatchedCptPolicyId) => void;
  onProvidersChanged?: () => void;
}

const PAGE_SIZE = 10;
const SUCCESS_MESSAGE_MS = 10000;
const DEFAULT_MESSAGE_MS = 3000;
const CODE_TYPE_OPTIONS = ["CPT", "HCPCS"] as const;
type CodeTypeOption = (typeof CODE_TYPE_OPTIONS)[number];

const normalizeCode = (value: string) => value.trim().toUpperCase();

type CptFlashMessage = {
  type: "success" | "error" | "info";
  text: string;
  resultFileContent?: string;
  resultFileName?: string;
};

const parseCodeInput = (raw: string): string[] => {
  const seen = new Set<string>();
  const codes: string[] = [];

  raw
    .split(/[\n,;\s]+/)
    .map(normalizeCode)
    .filter(Boolean)
    .forEach((code) => {
      if (!seen.has(code)) {
        seen.add(code);
        codes.push(code);
      }
    });

  return codes;
};

const CptCodesPanel = ({
  provider,
  importNonce = 0,
  compact = false,
  showUnmatchedPolicy = false,
  defaultProviderLabel,
  unmatchedPolicy,
  onUnmatchedPolicyChange,
  onProvidersChanged,
}: CptCodesPanelProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const listTopRef = useRef<HTMLDivElement>(null);
  const messageTimeoutRef = useRef<number | null>(null);

  const [codes, setCodes] = useState<string[]>(
    () => provider.assignedCodes || [],
  );
  const [draft, setDraft] = useState("");
  const [codeType, setCodeType] = useState<CodeTypeOption>("CPT");
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [internalUnmatchedPolicy, setInternalUnmatchedPolicy] =
    useState<UnmatchedCptPolicyId>(DEFAULT_UNMATCHED_CPT_POLICY);
  const [message, setMessage] = useState<CptFlashMessage | null>(null);
  const [codeToRemove, setCodeToRemove] = useState<string | null>(null);
  const [cptIdByCode, setCptIdByCode] = useState<Record<string, number>>({});
  const [loadingCodes, setLoadingCodes] = useState(false);

  const auditUserId = useAuditUserId();

  const providerId = Number(provider.id);

  const clearMessageTimeout = () => {
    if (messageTimeoutRef.current != null) {
      window.clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = null;
    }
  };

  useEffect(() => () => clearMessageTimeout(), []);

  const loadCodes = async () => {
    if (!providerId) return;
    setLoadingCodes(true);
    try {
      const result = await ProviderIntegrationApi.getCptCodes(providerId, 1, 10000);
      const items = result?.items || [];
      setCodes(items.map((item) => item.cptCode));
      setCptIdByCode(
        Object.fromEntries(items.map((item) => [item.cptCode, item.id])),
      );
    } catch {
      setCodes(provider.assignedCodes || []);
    } finally {
      setLoadingCodes(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, [provider.id]);

  const routeDefaultLabel =
    defaultProviderLabel || provider.shortName || provider.name;
  const unmatchedValue = unmatchedPolicy ?? internalUnmatchedPolicy;

  const handleUnmatchedPolicyChange = (value: UnmatchedCptPolicyId) => {
    if (onUnmatchedPolicyChange) {
      onUnmatchedPolicyChange(value);
      return;
    }
    setInternalUnmatchedPolicy(value);
  };

  const deferredFilter = useDeferredValue(filter.trim().toUpperCase());

  useEffect(() => {
    setCodes(provider.assignedCodes || []);
    setDraft("");
    setCodeType("CPT");
    setFilter("");
    setPage(1);
  }, [provider.id]);

  useEffect(() => {
    // Keep in sync when parent refreshes counts/codes without wiping flash messages.
    if (provider.assignedCodes?.length) {
      setCodes(provider.assignedCodes);
    }
  }, [provider.assignedCodes]);

  useEffect(() => {
    if (!importNonce) return;
    setMessage(null);
    window.setTimeout(() => {
      fileInputRef.current?.click();
    }, 0);
  }, [importNonce]);

  useEffect(() => {
    setPage(1);
  }, [deferredFilter]);

  const filteredCodes = useMemo(() => {
    const list = deferredFilter
      ? codes.filter((code) => code.includes(deferredFilter))
      : codes;
    return [...list].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [codes, deferredFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredCodes.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const pageCodes = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredCodes.slice(start, start + PAGE_SIZE);
  }, [filteredCodes, currentPage]);

  const rangeStart =
    filteredCodes.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filteredCodes.length);

  const draftCount = useMemo(() => parseCodeInput(draft).length, [draft]);

  const showMessage = (
    type: CptFlashMessage["type"],
    text: string,
    options?: {
      autoHideMs?: number;
      resultFileContent?: string;
      resultFileName?: string;
    },
  ) => {
    clearMessageTimeout();
    setMessage({
      type,
      text,
      resultFileContent: options?.resultFileContent,
      resultFileName: options?.resultFileName,
    });
    const hasDownload = Boolean(options?.resultFileContent);
    const hideAfter = hasDownload
      ? 0
      : options?.autoHideMs ??
        (type === "success" ? SUCCESS_MESSAGE_MS : DEFAULT_MESSAGE_MS);
    if (hideAfter > 0) {
      messageTimeoutRef.current = window.setTimeout(() => {
        setMessage(null);
        messageTimeoutRef.current = null;
      }, hideAfter);
    }
  };

  const dismissMessage = () => {
    clearMessageTimeout();
    setMessage(null);
  };

  const handleDownloadResultFile = (content: string, fileName?: string) => {
    downloadTextFile(
      content,
      fileName ||
        buildSkippedDownloadFilename(provider.shortName || provider.name),
    );
  };

  const runBulkAdd = async (
    batches: Array<{ codeType: CodeTypeOption; codes: string[] }>,
    createdBy: string,
  ) => {
    const results: BulkAddCptCodesResponseApi[] = [];

    for (const batch of batches) {
      if (!batch.codes.length) continue;
      const result = await ProviderIntegrationApi.addCptCodes(providerId, {
        codes: batch.codes,
        codeType: batch.codeType,
        createdBy,
      });
      results.push(result);
    }

    return { merged: mergeAddResults(results) };
  };

  const focusComposer = () => {
    window.setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const scrollListTop = () => {
    listTopRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  };

  const handleClearDraft = () => {
    setDraft("");
    dismissMessage();
  };

  const handleValidateAndAdd = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!providerId) return;

    const createdBy = auditUserId;

    try {
      const nextCodes = parseCodeInput(draft);
      if (!nextCodes.length) {
        showMessage("error", "Enter at least one CPT/HCPCS code.");
        return;
      }
      const { merged: mergedResult } = await runBulkAdd(
        [{ codeType, codes: nextCodes }],
        createdBy,
      );

      setDraft("");
      setPage(1);
      await loadCodes();
      onProvidersChanged?.();

      const summary = buildAddResultSummary(mergedResult);
      const text = formatAddResultMessage(mergedResult);
      const fileContent =
        mergedResult.resultFileContent ??
        (mergedResult as { ResultFileContent?: string }).ResultFileContent;
      const fileName =
        mergedResult.resultFileName ??
        (mergedResult as { ResultFileName?: string }).ResultFileName ??
        undefined;

      const downloadOpts =
        fileContent && (summary.duplicateCount > 0 || summary.conflictCount > 0)
          ? { resultFileContent: fileContent, resultFileName: fileName }
          : undefined;

      if (summary.added > 0 && !downloadOpts) {
        showMessage("success", text);
      } else if (summary.added > 0) {
        showMessage("success", text, downloadOpts);
      } else if (downloadOpts) {
        showMessage("error", text, downloadOpts);
      } else {
        showMessage("error", text || "No codes were added.");
      }
      scrollListTop();
    } catch {
      showMessage("error", "Unable to add codes.");
    }
  };

  const handleRemoveRequest = (code: string) => {
    setCodeToRemove(code);
  };

  const handleRemoveCancel = () => {
    setCodeToRemove(null);
  };

  const handleRemoveConfirm = async () => {
    if (!codeToRemove || !providerId) return;
    const code = codeToRemove;
    const cptId = cptIdByCode[code];
    setCodeToRemove(null);

    const userId = auditUserId;

    try {
      if (cptId) {
        await ProviderIntegrationApi.deleteCptCode(providerId, cptId, userId);
      }
      setCodes((prev) => prev.filter((item) => item !== code));
      setCptIdByCode((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      onProvidersChanged?.();
      showMessage("success", `Removed ${code}.`);
    } catch {
      showMessage("error", `Unable to remove ${code}.`);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !providerId) return;

    try {
      const result = await ProviderIntegrationApi.importCptCodes(
        providerId,
        file,
        auditUserId,
      );

      setDraft("");
      setPage(1);
      await loadCodes();
      onProvidersChanged?.();

      const summary = buildAddResultSummary(result);
      const text = formatAddResultMessage(result, { prefix: "Import: " });
      const fileContent =
        result.resultFileContent ??
        (result as { ResultFileContent?: string }).ResultFileContent;
      const fileName =
        result.resultFileName ??
        (result as { ResultFileName?: string }).ResultFileName ??
        undefined;

      const downloadOpts =
        fileContent && (summary.duplicateCount > 0 || summary.conflictCount > 0)
          ? { resultFileContent: fileContent, resultFileName: fileName }
          : undefined;

      if (summary.added > 0 && !downloadOpts) {
        showMessage("success", text);
      } else if (summary.added > 0) {
        showMessage("success", text, downloadOpts);
      } else if (downloadOpts) {
        showMessage("error", text, downloadOpts);
      } else {
        showMessage("error", text || "No codes were imported.");
      }
      scrollListTop();
    } catch (error: unknown) {
      const apiMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || "Unable to import CPT codes.";
      showMessage("error", apiMessage);
    }
  };

  const handlePasteList = async () => {
    focusComposer();
    try {
      const text = await navigator.clipboard.readText();
      if (text?.trim()) {
        setDraft((prev) =>
          prev.trim() ? `${prev.trim()}\n${text.trim()}` : text.trim(),
        );
        showMessage(
          "info",
          "Pasted codes from clipboard. Review, then add.",
        );
      }
    } catch {
      showMessage("info", "Paste codes into the field below.");
    }
  };

  const goToPage = (nextPage: number) => {
    setPage(Math.min(Math.max(1, nextPage), totalPages));
    scrollListTop();
  };

  return (
    <div className={`ic-cpt-panel${compact ? " ic-cpt-panel-compact" : ""}`}>
      <div className="ic-cpt-shell">
        <header className="ic-cpt-header">
          <div className="ic-cpt-header-main">
            <span className="ic-stats-icon" aria-hidden="true">
              <i className="fh_docs_fill"></i>
            </span>
            <div className="ic-cpt-header-text">
              <div className="ic-cpt-title-line">
                <h2 className="ic-cpt-title">CPT Codes</h2>
              </div>
              <p className="ic-cpt-subtitle mb-0">
                Built for large catalogs — search, page, and import hundreds of
                codes at once.
              </p>
            </div>
          </div>
        </header>

        {loadingCodes && (
          <div className="ic-inline-message" role="status">
            Loading CPT codes...
          </div>
        )}

        <div className="ic-cpt-flash-slot" aria-live="polite">
          {message && (
            <div
              className={`ic-cpt-message ic-cpt-message-${message.type}`}
              role="status"
            >
              <i
                className={
                  message.type === "error"
                    ? "fh_error_line"
                    : message.type === "success"
                      ? "fh_check_circle_line"
                      : "fh_info_fill"
                }
                aria-hidden="true"
              ></i>
              <span className="ic-cpt-message-text">{message.text}</span>
              {message.resultFileContent && (
                <div className="ic-cpt-message-actions">
                  <button
                    type="button"
                    className="btn btn-border-primary btn-xs"
                    onClick={() =>
                      handleDownloadResultFile(
                        message.resultFileContent!,
                        message.resultFileName,
                      )
                    }
                  >
                    Download list
                  </button>
                  <button
                    type="button"
                    className="ic-cpt-message-dismiss"
                    onClick={dismissMessage}
                    aria-label="Dismiss message"
                  >
                    <i className="fh_close" aria-hidden="true"></i>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ic-cpt-body">
          <form className="ic-cpt-composer" onSubmit={handleValidateAndAdd}>
            <div className="ic-cpt-section-head">
              <h3 className="ic-cpt-section-title">Add codes</h3>
              <div className="ic-cpt-entry-actions">
                <button
                  type="button"
                  className="btn btn-border-primary btn-xs ic-cpt-tool-btn"
                  onClick={handleImportClick}
                >
                  Import CSV
                </button>
              </div>
            </div>

            <div className="ic-cpt-code-type">
              <label className="ic-cpt-code-type-label" htmlFor="ic-cpt-code-type">
                Code type
              </label>
              <select
                id="ic-cpt-code-type"
                className="form-select form-select-sm ic-cpt-code-type-select"
                value={codeType}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                  setCodeType(event.target.value as CodeTypeOption);
                  setMessage(null);
                }}
                aria-label="Code type"
              >
                {CODE_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <textarea
              ref={textareaRef}
              className="form-control ic-cpt-textarea"
              rows={4}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                dismissMessage();
              }}
              placeholder="Paste codes manually, or import CSV using CodeType|Code"
              aria-label="CPT or HCPCS codes to add"
            />

            <p className="ic-cpt-helper mb-0">
              Import CSV format: CodeType|Code (provider is taken from the selected
              provider). If duplicates or ownership conflicts occur, use Download
              list on the result message.
            </p>

            <div className="ic-cpt-add-actions">
              <button
                type="button"
                className="btn btn-border-primary btn-sm ic-cpt-clear-btn"
                onClick={handleClearDraft}
                disabled={!draft.trim()}
                aria-label="Clear draft codes"
                title="Clear"
              >
                <span>Clear</span>
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={!draft.trim()}
              >
                Add
                {draftCount > 0 ? ` (${draftCount.toLocaleString()})` : ""}
              </button>
            </div>
          </form>

          <section className="ic-cpt-list-section" aria-label="Assigned codes">
            <div className="ic-cpt-section-head" ref={listTopRef}>
              <h3 className="ic-cpt-section-title">Assigned codes</h3>
              <div className="ic-cpt-list-controls">
                <div className="ic-filter-input-wrap ic-cpt-filter-wrap">
                  <i className="fh_search ic-filter-input-icon" aria-hidden="true"></i>
                  <input
                    type="text"
                    className="form-control form-control-sm ic-cpt-filter ic-filter-input"
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                    placeholder="Search codes..."
                    aria-label="Search assigned CPT codes"
                  />
                  {filter && (
                    <button
                      type="button"
                      className="ic-filter-clear-btn"
                      onClick={() => setFilter("")}
                      aria-label="Clear filter"
                      title="Clear"
                    >
                      <i className="fh_close" aria-hidden="true"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {filteredCodes.length === 0 ? (
              <div className="ic-cpt-empty-state">
                <div className="ic-cpt-empty-icon" aria-hidden="true">
                  <i className="fh_docs_fill"></i>
                </div>
                <p className="ic-cpt-empty-title mb-1">
                  {codes.length === 0
                    ? "No codes assigned yet"
                    : "No matching codes"}
                </p>
                <p className="ic-cpt-empty-text mb-0">
                  {codes.length === 0
                    ? "Import a CSV or paste a large list to assign codes quickly."
                    : "Try a different search to find an assigned code."}
                </p>
              </div>
            ) : (
              <div className="ic-cpt-table-wrap">
                <table className="table ic-cpt-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">CPT / HCPCS</th>
                      <th scope="col" className="text-end">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageCodes.map((code) => (
                      <tr key={code}>
                        <td>
                          <span className="ic-cpt-code">{code}</span>
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="ic-cpt-remove-btn"
                            onClick={() => handleRemoveRequest(code)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {filteredCodes.length > 0 && (
              <div className="ic-cpt-list-footer">
                <span className="ic-cpt-list-meta">
                  {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of{" "}
                  {filteredCodes.length.toLocaleString()}
                  {deferredFilter
                    ? ` match${filteredCodes.length === 1 ? "" : "es"}`
                    : " codes"}
                  {deferredFilter && codes.length !== filteredCodes.length
                    ? ` · ${codes.length.toLocaleString()} total`
                    : ""}
                </span>

                <nav
                  aria-label="CPT codes page navigation"
                  className="ic-cpt-pagination d-flex align-items-center"
                >
                  <span className="pagination-info me-2">
                    Page{" "}
                    <strong>
                      {currentPage} of {totalPages}
                    </strong>
                  </span>
                  <ul className="pagination d-flex justify-content-end mb-0">
                    <li className="page-item">
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => goToPage(1)}
                        disabled={currentPage <= 1}
                        aria-label="First page"
                      >
                        <i className="fh_double_arrow_left"></i>
                      </button>
                    </li>
                    <li className="page-item">
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage <= 1}
                        aria-label="Previous page"
                      >
                        <i className="fh_arrow_left_line"></i>
                      </button>
                    </li>
                    <li className="page-item">
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        aria-label="Next page"
                      >
                        <i className="fh_arrow_right_line"></i>
                      </button>
                    </li>
                    <li className="page-item">
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => goToPage(totalPages)}
                        disabled={currentPage >= totalPages}
                        aria-label="Last page"
                      >
                        <i className="fh_double_arrow_right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </section>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          className="d-none"
          onChange={handleFileChange}
        />

        {showUnmatchedPolicy && (
          <UnmatchedCptCodes
            value={unmatchedValue}
            defaultProviderLabel={routeDefaultLabel}
            onChange={handleUnmatchedPolicyChange}
          />
        )}
      </div>

      <Modal
        show={Boolean(codeToRemove)}
        onHide={handleRemoveCancel}
        centered
        size="sm"
        backdrop="static"
        keyboard={false}
        className="custom-pop-up about-me-popup ic-cpt-remove-popup"
        aria-labelledby="ic-cpt-remove-title"
      >
        <Modal.Body>
          <p className="mb-0 text-center">
            Are you sure you want to remove{" "}
            <strong className="ic-cpt-code">{codeToRemove}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <button
            type="button"
            className="btn btn-border-primary btn-sm me-2"
            onClick={handleRemoveCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleRemoveConfirm}
          >
            Remove
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CptCodesPanel;
